import * as _ from 'lodash';

import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { interval, Subject } from 'rxjs';
import { debounce, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MatrixType } from './matrix.module';
import { GroupedHeader, OrderMatrixHeadersComponent } from './matrix-headers.component';
import { DataSource, InteractiveCell, ReadyOnlyCell } from '../types';
import { MatrixCellType } from './cell/cell.component';
import { OrderInputComponent } from './input/input.component';
import { TooltipPosition, TooltipPositionX, TooltipPositionY } from 'fc-ui-components';
import { filterDataSource } from './utils';
import { MatrixErrorTypes } from 'app/b2b-order/services/order-product-detail/ui-mapper';
import { LanguageService } from 'services';
import { getMatrixSelectedAmount, isNumberedSize } from 'app/b2b-order/helpers/order';

export type CopyEvent = {
    sourceStoreId: string;
    columnIndex: number;
};

@Component({
    selector: 'fc-order-matrix',
    templateUrl: './matrix.component.html',
    styleUrls: ['./matrix.component.less'],
    standalone: false
})
export class OrderMatrixComponent implements OnInit, OnChanges, OnDestroy {
    @Input() matrixType: MatrixType;
    @Input() dataSource: DataSource = [[]];
    @Input() set config(configValues) {
        this.matrixConfig = {
            ...this.matrixConfig,
            ...(configValues || {})
        };
    }

    matrixConfig: MatrixConfig = {
        showHeader: true,
        showFooter: false,
        capitalizeHeaders: true,
        filterDataSource: true,
        monitorStatusChanges: false,
        useTooltip: false,
        sizePrefix: true,
        showErrors: false,
        useGroupedHeaders: false,
        groupedHeaders: undefined as GroupedHeader[],
        capitalizeLeftHeaders: true,
        zebraStyle: false,
        hideFirstCell: false,
        skipEmptyLeftHeaders: false,
        useStickyHeader: true,
        showCopy: false,
        wideInput: false
    };

    @ViewChild(OrderMatrixHeadersComponent) headersViewChild: OrderMatrixHeadersComponent;
    @ViewChild('matrixTable', { static: true, read: ElementRef }) matrixTable: ElementRef;

    @Output() onCopy: EventEmitter<CopyEvent>;
    @Output() dataChanges: EventEmitter<MatrixStateControl>;
    @Output('blur') blurChanges: EventEmitter<MatrixStateControl>;
    @Output() isScrolling = new EventEmitter<boolean>();

    private destroySubject: Subject<boolean>;
    private lastModified: { row: number; column: number; isValid: boolean } = null;
    public cellTooltipPosition: TooltipPosition = {
        originX: TooltipPositionX.Center,
        originY: TooltipPositionY.Bottom
    };
    public backorderIconTooltipVisibilityMap = {};
    public backorderIconTooltipPosition: TooltipPosition = {
        originX: TooltipPositionX.Center,
        originY: TooltipPositionY.Top
    };
    public cellTooltipDisplay: Array<Array<boolean>> = [[]];
    public groupedHeadersSizes: Array<number> = [];

    leftHeaders: Array<string> = [];
    topHeaders: Array<string> = [];
    topHeadersTooltip: Array<string> = [];
    leftHeadersTooltip: Array<string> = [];
    topHeaderStoreIds: Array<string> = [];
    topHeadersHoverDisplayMap: { [size: string]: string } = {};
    form: FormGroup<{ rows: FormArray<FormGroup<any>> }>;
    isMultiStore = false;

    private errors: { [key: string]: string } = {};
    private errorMessages: { [key: string]: string } = {};

    constructor(
        private fb: FormBuilder,
        private languageService: LanguageService
    ) {
        this.dataChanges = new EventEmitter();
        this.blurChanges = new EventEmitter();
        this.destroySubject = new Subject();
        this.onCopy = new EventEmitter();
    }

    ngOnInit() {
        if (this.matrixConfig.showErrors) {
            const incrementErrorKey = 'ORDER.MATRIX.INCREMENT_ERROR';
            const stockErrorKey = 'ORDER.MATRIX.STOCK_ERROR';

            this.languageService.translateObservable([incrementErrorKey, stockErrorKey]).subscribe((translations) => {
                this.errorMessages = {
                    [MatrixErrorTypes.INVALID_INCREMENT]: translations[incrementErrorKey],
                    [MatrixErrorTypes.INVALID_STOCK]: translations[stockErrorKey]
                };
            });
        }
    }

    ngOnChanges(): void {
        this.form = this.fb.group({
            rows: this.fb.array([] as Array<FormGroup>)
        });

        // Clear hover display map to remove stale entries when data source changes
        this.topHeadersHoverDisplayMap = {};

        if (_.isEmpty(this.filteredDataSource)) {
            return;
        }

        if (!_.isEmpty(this.form?.controls?.rows?.controls)) {
            this.destroySubject.next(true);
        }

        if (this.matrixConfig.monitorStatusChanges) {
            this.monitorStatusChanges();
        }

        // Reorder data when grouped headers are used
        if (this.matrixConfig.useGroupedHeaders && this.matrixConfig.groupedHeaders) {
            this.generateGroupedHeadersSizes();
            this.reorderDataForGroupedHeaders();
        }

        const leftHeaders = new Array<string>();
        const topHeaders = new Array<string>();
        const topHeadersTooltip = new Array<string>();
        const leftHeadersTooltip = new Array<string>();
        const topHeaderStoreIds = new Array<string>();
        this.resetCellTooltipDisplay();
        this.isMultiStore = false;

        for (const [rowIndex, row] of this.filteredDataSource.entries()) {
            const rowForm = this.fb.group({
                row: this.fb.array([] as Array<FormGroup>)
            });

            const errorMessagesMap: { [key: string]: Array<string | undefined> } = {};

            for (const [cellIndex, cell] of row.entries()) {
                const cellForm = this.fb.group({
                    input: [
                        ...(this.isInteractiveCell(cell)
                            ? [
                                  { value: cell.selectedQuantity, disabled: cell.disabled },
                                  Array.isArray(cell.validators)
                                      ? cell.validators.filter((validator) => validator)
                                      : cell.validators
                              ]
                            : [-1])
                    ]
                });

                if (!leftHeaders[rowIndex]) {
                    leftHeaders[rowIndex] = cell && 'leftLabel' in cell ? cell.leftLabel : null;
                }

                if (!topHeaders[cellIndex]) {
                    topHeaders[cellIndex] = cell ? cell.topLabel : null;
                    topHeadersTooltip[cellIndex] = cell ? (cell as ReadyOnlyCell).tooltip?.topLabel : null;
                    topHeaderStoreIds[cellIndex] = this.isInteractiveCell(cell) ? cell.storeId : null;
                    this.isMultiStore = this.isMultiStore || !!topHeaderStoreIds[cellIndex];
                }

                // Build hover texts map
                if (cell && cell.hoverText) {
                    const mapKey = this.getMapKeyFromCell(cell);
                    if (mapKey) {
                        this.topHeadersHoverDisplayMap[mapKey] = cell.hoverText;
                    }
                }

                rowForm.controls.row.push(cellForm);

                if (this.isInteractiveCell(cell) && this.matrixConfig.showErrors) {
                    const errorMessage = this.getErrorMessage(cell, cellForm);

                    if (cell.id in errorMessagesMap) {
                        errorMessagesMap[cell.id].push(errorMessage);
                    } else {
                        errorMessagesMap[cell.id] = [errorMessage];
                    }
                }

                cellForm.valueChanges
                    .pipe(
                        distinctUntilChanged(),
                        debounce((i) => interval(30)),
                        takeUntil(this.destroySubject)
                    )
                    .subscribe((inputValue) => {
                        const cellReference = this.filteredDataSource[rowIndex][cellIndex];
                        if (this.isInteractiveCell(cellReference)) {
                            this.lastModified = { row: rowIndex, column: cellIndex, isValid: this.form.valid };
                            this.handleInputValueChange(cellReference, inputValue);

                            if (this.matrixConfig.showErrors) {
                                this.handleErrors(cellReference, cellForm);
                            }
                        }
                    });
            }

            Object.entries(errorMessagesMap).forEach(([cellId, errorMessages]) => {
                const validErrors = errorMessages.filter((errorMessage) => errorMessage);

                if (validErrors.length) {
                    this.errors[cellId] = validErrors[0];
                } else {
                    delete this.errors[cellId];
                }
            });

            this.topHeaderStoreIds = topHeaderStoreIds;
            this.setHeaders(leftHeaders, topHeaders, leftHeadersTooltip, topHeadersTooltip);
            this.rows.push(rowForm);
        }
    }

    ngOnDestroy() {
        this.destroySubject.next(true);
        this.destroySubject.complete();
    }

    public toggleBackorderIconTooltip(rowIndex: number, columnIndex: number, visible: boolean) {
        this.backorderIconTooltipVisibilityMap[`${rowIndex}-${columnIndex}`] = visible;
    }

    public isBackorderIconTooltipVisible(rowIndex: number, columnIndex: number): boolean {
        return this.backorderIconTooltipVisibilityMap[`${rowIndex}-${columnIndex}`] ?? false;
    }

    // Responsible for filtering the single size and prepack matrix rows
    get filteredDataSource() {
        if (!this.matrixConfig.filterDataSource) {
            return this.dataSource;
        }

        return filterDataSource(this.matrixType, this.dataSource);
    }

    get rows() {
        return this.form.controls.rows as FormArray<FormGroup<any>>;
    }

    public getRow(index: number) {
        return this.form.controls.rows.at(index).controls.row as FormArray<FormGroup<any>>;
    }

    public getCell(rowIndex: number, columnIndex: number) {
        return this.filteredDataSource[rowIndex][columnIndex];
    }

    public getPrepackLabels(rowIndex: number, columnIndex: number) {
        const dataCell = this.getCell(rowIndex, columnIndex);

        return dataCell && 'items' in dataCell
            ? dataCell.items.map((item) => ({ name: item.text, count: item.quantity }))
            : [];
    }

    public getCellTooltip(rowIndex: number, columnIndex: number) {
        const dataCell = this.getCell(rowIndex, columnIndex);
        return _.get(dataCell, 'tooltip.cell', null);
    }

    public resetCellTooltipDisplay() {
        this.cellTooltipDisplay = Array.from({ length: this.filteredDataSource.length }, () =>
            Array.from({ length: this.filteredDataSource[0].length }, () => false)
        );
    }

    public cellExists(rowIndex: number, columnIndex: number) {
        return Boolean(this.getCell(rowIndex, columnIndex));
    }

    public getPlaceholder(rowIndex: number, columnIndex: number) {
        const dataCell = this.getCell(rowIndex, columnIndex);
        return this.isInteractiveCell(dataCell) ? `${dataCell.leftLabel}\${dataCell.topLabel}` : '';
    }

    public get MatrixType() {
        return MatrixType;
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public getPrepackTitle(rowIndex: number) {
        const [input, labels] = this.filteredDataSource[rowIndex] as [InteractiveCell, ReadyOnlyCell];

        const articlesInPrepack = labels.items.reduce((acc, item) => acc + item.quantity, 0);
        const totalItems = getMatrixSelectedAmount(input.selectedQuantity) * articlesInPrepack;

        return { itemCount: articlesInPrepack, totalItems };
    }

    public isPrepack() {
        return this.matrixType === MatrixType.Prepacks;
    }

    public isFooterCell(rowIndex: number) {
        return this.matrixConfig.showFooter && rowIndex === this.dataSource.length - 1;
    }

    onScroll(event: Event) {
        this.headersViewChild.onScroll(event);
        this.isScrolling.emit(true);
    }

    public toggleTooltip(value: boolean, rowIndex: number, columnIndex: number) {
        const cellTooltip = this.getCellTooltip(rowIndex, columnIndex);
        if (cellTooltip) {
            this.cellTooltipDisplay[rowIndex][columnIndex] = value;
        }
    }

    public onInputBlur(event: FocusEvent) {
        const blurredOutsideEditableInputs = !this.matrixTable.nativeElement.contains(event.relatedTarget);
        if (blurredOutsideEditableInputs) {
            this.blurChanges.emit({
                matrixState: this.filteredDataSource,
                formStatus: {
                    isValid: this.form.valid,
                    isInvalid: this.form.invalid,
                    isPending: this.form.pending
                }
            });
        }
    }

    public getCellType(cell: ReadyOnlyCell, rowIndex: number) {
        if (cell?.cellType) {
            return cell.cellType;
        }

        if (this.isPrepack()) {
            return MatrixCellType.BodyPrepack;
        }

        return MatrixCellType.BodyText;
    }

    public onCopyDropdownMenuClick(event: CopyEvent) {
        this.onCopy.emit(event);
    }

    public inputKeyboardNavigated(direction: KeyboardNavigateDirection, rowIndex: number, columnIndex: number) {
        const numberOfColumns = this.rows.value[0].row.length + 50;
        const numberOfRows = this.rows.value.length + 50;

        const isCellDisabled = (rowIndex: number, columnIndex: number) => {
            if (!this.filteredDataSource[rowIndex] || !this.getCell(rowIndex, columnIndex)) {
                return false;
            }
            const cell = this.getCell(rowIndex, columnIndex);
            return 'disabled' in cell && cell.disabled;
        };
        const isCellDisabledOrNotExists = (rowIndex: number, columnIndex: number) => {
            if (!this.filteredDataSource[rowIndex] || !this.getCell(rowIndex, columnIndex)) {
                return true;
            }
            return isCellDisabled(rowIndex, columnIndex);
        };

        const firstColumn = this.isMultiStore ? 1 : 0;

        switch (direction) {
            case KeyboardNavigateDirection.NextRow:
                rowIndex++;
                while (rowIndex < numberOfRows && isCellDisabledOrNotExists(rowIndex, columnIndex)) {
                    rowIndex++;
                }
                break;
            case KeyboardNavigateDirection.PreviousRow:
                rowIndex--;
                while (rowIndex >= 0 && isCellDisabledOrNotExists(rowIndex, columnIndex)) {
                    rowIndex--;
                }
                break;
            case KeyboardNavigateDirection.NextColumn:
                columnIndex++;
                while (columnIndex < numberOfColumns && isCellDisabledOrNotExists(rowIndex, columnIndex)) {
                    columnIndex++;
                }
                break;
            case KeyboardNavigateDirection.PreviousColumn:
                columnIndex--;
                while (columnIndex >= firstColumn && isCellDisabledOrNotExists(rowIndex, columnIndex)) {
                    columnIndex--;
                }
                break;
        }

        if (columnIndex < firstColumn || rowIndex < 0 || columnIndex > numberOfColumns || rowIndex >= numberOfRows) {
            return;
        }

        const cell = this.filteredDataSource[rowIndex][columnIndex];
        if (cell && 'id' in cell) {
            document.getElementById(OrderInputComponent.getInputId(rowIndex, columnIndex, cell.id))?.focus();
        }
    }

    public capitalizeHeaders() {
        return !this.isMultiStore && this.matrixConfig.capitalizeHeaders;
    }

    public getErrors() {
        return Object.values(this.errors);
    }

    /**
     * Extracts the appropriate map key from a cell for headerHoverDisplayMap.
     *
     * For grouped headers (2D sizes like W30/L30):
     * - InteractiveCell: id = "qty-30/30", topLabel = "30" → returns "30/30"
     * - ReadOnlyCell: topLabel = "30/30" → returns "30/30"
     *
     * For standard headers:
     * - Cell: topLabel = "S" → returns "S"
     */
    private getMapKeyFromCell(cell: InteractiveCell | ReadyOnlyCell | null): string | null {
        if (!cell?.topLabel) {
            return null;
        }

        // For standard headers, topLabel is already the size
        if (!this.matrixConfig.useGroupedHeaders) {
            return cell.topLabel;
        }

        // For grouped headers, extract the full size (waist/length)
        if (this.isInteractiveCell(cell)) {
            return this.extractSizeFromInteractiveCellId(cell) ?? cell.topLabel;
        }

        // ReadOnlyCell: topLabel already contains the full size
        return cell.topLabel;
    }

    /**
     * Extracts size from InteractiveCell id (e.g., "qty-30/30" → "30/30")
     */
    private extractSizeFromInteractiveCellId(cell: InteractiveCell): string | null {
        if (typeof cell.id !== 'string') {
            return null;
        }

        const searchResult = cell.id.search(/^qty-/);
        return searchResult === 0 && cell.id.length > 4 ? cell.id.substring(4) : null;
    }

    private generateGroupedHeadersSizes() {
        this.groupedHeadersSizes = [];
        let previousGroupSize = 0;

        this.matrixConfig.groupedHeaders.forEach((group, index) => {
            previousGroupSize += group.childLabels.length;

            if (index !== this.matrixConfig.groupedHeaders.length - 1) {
                this.groupedHeadersSizes.push(previousGroupSize);
            }
        });
    }

    private reorderDataForGroupedHeaders() {
        if (!this.matrixConfig.groupedHeaders || this.filteredDataSource.length === 0) {
            return;
        }

        const groupedHeaders = this.matrixConfig.groupedHeaders;
        const originalRows = [...this.filteredDataSource];
        const expectedOrder: number[] = [];

        for (const group of groupedHeaders) {
            for (const childLabel of group.childLabels) {
                // Find the original position of cells that match this parent/child combination
                const originalRow = originalRows[0]; // Use first row to determine positions

                for (let i = 0; i < originalRow.length; i++) {
                    const cell = originalRow[i];
                    const fullSize = `${group.parentLabel}/${childLabel}`;

                    // For interactive cells, topLabel contains just the waist part
                    // For view-only cells, topLabel contains the full size
                    const isMatchingCell =
                        ('topLabel' in cell && cell.topLabel === group.parentLabel) ||
                        ('topLabel' in cell && cell.topLabel === fullSize);

                    if (isMatchingCell) {
                        const hasMatchingSize = originalRows.some((row) => {
                            const rowCell = row[i];
                            if (!rowCell) return false;

                            // Check ReadyOnlyCell (has topLabel with full size)
                            if ('topLabel' in rowCell && rowCell.topLabel === fullSize) {
                                return true;
                            }

                            // Check InteractiveCell (has id property)
                            if ('id' in rowCell && rowCell.id === `qty-${fullSize}`) {
                                return true;
                            }

                            return false;
                        });

                        if (hasMatchingSize) {
                            expectedOrder.push(i);
                            break; // Move to next childLabel
                        }
                    }
                }
            }
        }

        // Reorder all rows based on the expected order
        for (let rowIndex = 0; rowIndex < this.filteredDataSource.length; rowIndex++) {
            const originalRow = _.cloneDeep(originalRows[rowIndex]);
            const reorderedRow = [];

            for (const index of expectedOrder) {
                reorderedRow.push(originalRow[index]);
            }

            if (reorderedRow.length !== originalRow.length) {
                console.error('[RPL] Data lost while matrix reordering rows', originalRow, reorderedRow);
            }

            this.filteredDataSource[rowIndex] = reorderedRow;
        }
    }

    private isInteractiveCell(cell: InteractiveCell | ReadyOnlyCell | null): cell is InteractiveCell {
        return cell && 'id' in cell;
    }

    private setHeaders(
        leftHeaders: Array<string>,
        topHeaders: Array<string>,
        leftHeadersTooltip: Array<string>,
        topHeadersTooltip: Array<string>
    ) {
        if (this.matrixType === MatrixType.SingleSize) {
            this.topHeaders = topHeaders.filter(Boolean).length ? topHeaders : ['Qty'];
            this.leftHeaders = leftHeaders;
            this.topHeadersTooltip = topHeadersTooltip;
            this.leftHeadersTooltip = leftHeadersTooltip;
        } else if (this.matrixType === MatrixType.Prepacks) {
            this.topHeaders = ['Qty', 'Included Sizes'];
            this.leftHeaders = leftHeaders;
        } else {
            this.topHeaders = _.map(topHeaders, (size: string) => size);
            this.leftHeaders = _.map(leftHeaders, (size: string) =>
                isNumberedSize(size) ? (this.matrixConfig.sizePrefix ? `L${size}` : size) : size
            );
            this.topHeadersTooltip = topHeadersTooltip;
            this.leftHeadersTooltip = leftHeadersTooltip;
        }
    }

    private handleInputValueChange(cell: InteractiveCell, inputValue) {
        cell.selectedQuantity = inputValue.input;
        this.dataChanges.emit({
            matrixState: this.filteredDataSource,
            updatedCell: cell,
            formStatus: {
                isValid: this.form.valid,
                isInvalid: this.form.invalid,
                isPending: this.form.pending
            }
        });
    }

    private handleErrors(cell: InteractiveCell, cellForm: FormGroup): void {
        const errorMessage = this.getErrorMessage(cell, cellForm);

        if (errorMessage) {
            this.errors[cell.id] = errorMessage;
        } else {
            delete this.errors[cell.id];
        }
    }

    private getErrorMessage(cell: InteractiveCell, cellForm: FormGroup): string {
        if (cell.disabled) return;

        const errors = cellForm.controls.input.errors;

        if (!errors) {
            return;
        }

        let cellName = cell.leftLabel;

        if (cell.topLabel && cell.topLabel !== 'Qty') {
            const areNumberSizes = [cell.leftLabel, cell.topLabel].every(isNumberedSize);
            cellName = areNumberSizes ? `L${cell.leftLabel} W${cell.topLabel}` : `${cell.leftLabel} ${cell.topLabel}`;
        }

        const incrementError = errors[MatrixErrorTypes.INVALID_INCREMENT];
        const stockError = errors[MatrixErrorTypes.INVALID_STOCK];

        if (incrementError) {
            return this.errorMessages[MatrixErrorTypes.INVALID_INCREMENT]
                .replace('{{increment}}', incrementError.increment)
                .replace('{{size}}', cellName);
        }

        if (stockError) {
            let stockErrorMessage = this.errorMessages[MatrixErrorTypes.INVALID_STOCK].replace('{{size}}', cellName);

            if (this.isMultiStore) {
                // Cannot use stockError.max because it depends on the selected quantities in other stores
                stockErrorMessage = stockErrorMessage.replace(' ({{stock}})', '');
            } else {
                stockErrorMessage = stockErrorMessage.replace('{{stock}}', stockError.max);
            }

            return stockErrorMessage;
        }
    }

    private monitorStatusChanges() {
        this.form.statusChanges
            .pipe(
                distinctUntilChanged(),
                debounce((i) => interval(30)),
                takeUntil(this.destroySubject)
            )
            .subscribe(() => {
                if (this.lastModified) {
                    const { row: rowIndex, column: cellIndex } = this.lastModified;
                    const cellReference = this.filteredDataSource[rowIndex][cellIndex];

                    if (this.isInteractiveCell(cellReference) && this.form.valid !== this.lastModified.isValid) {
                        this.lastModified = null;
                        this.handleInputValueChange(cellReference, { input: cellReference.selectedQuantity });
                    }
                }
            });
    }
}

export interface MatrixStateControl {
    matrixState: DataSource;
    updatedCell?: InteractiveCell;
    formStatus: {
        isValid: boolean;
        isInvalid: boolean;
        isPending: boolean;
    };
}

export enum KeyboardNavigateDirection {
    PreviousRow = 'previousRow',
    NextRow = 'nextRow',
    NextColumn = 'nextColumn',
    PreviousColumn = 'previousColumn'
}

export enum MatrixIcons {
    Shop = 'far shop'
}

export type MatrixConfig = {
    showHeader?: boolean;
    showFooter?: boolean;
    capitalizeHeaders?: boolean;
    filterDataSource?: boolean;
    monitorStatusChanges?: boolean;
    useTooltip?: boolean;
    sizePrefix?: boolean;
    showErrors?: boolean;
    useGroupedHeaders?: boolean;
    groupedHeaders?: GroupedHeader[];
    invertGroupedHeaders?: boolean;
    capitalizeLeftHeaders?: boolean;
    zebraStyle?: boolean;
    hideFirstCell?: boolean;
    skipEmptyLeftHeaders?: boolean;
    useStickyHeader?: boolean;
    showCopy?: boolean;
    enableHoverAndCopy?: boolean;
    wideInput?: boolean;
};
